// Система истории действий пользователя
export interface HistoryItem {
  id: string;
  userId: string;
  action: 'calculation' | 'vaccination' | 'atlas' | 'emergency' | 'growth' | 'ai_assistant';
  title: string;
  description: string;
  data: any;
  timestamp: Date;
  section: string;
  isFavorite: boolean;
}

export interface CalculationHistory extends HistoryItem {
  action: 'calculation';
  data: {
    medication: string;
    age: number;
    weight: number;
    indication: string;
    result: {
      dose: number;
      unit: string;
      frequency: string;
      duration: string;
      notes: string[];
    };
  };
}

export interface VaccinationHistory extends HistoryItem {
  action: 'vaccination';
  data: {
    age: number;
    vaccines: string[];
    schedule: any;
  };
}

export interface AtlasHistory extends HistoryItem {
  action: 'atlas';
  data: {
    disease: string;
    symptoms: string[];
    searchTerm: string;
  };
}

export interface EmergencyHistory extends HistoryItem {
  action: 'emergency';
  data: {
    condition: string;
    priority: string;
    algorithm: string;
  };
}

export interface GrowthHistory extends HistoryItem {
  action: 'growth';
  data: {
    age: number;
    weight: number;
    height: number;
    gender: 'male' | 'female';
    percentile: any;
  };
}

export interface AIAssistantHistory extends HistoryItem {
  action: 'ai_assistant';
  data: {
    analysisType: string;
    input: string;
    result: string;
    confidence: number;
  };
}

// Менеджер истории
export class HistoryManager {
  private static instance: HistoryManager;
  private history: HistoryItem[] = [];

  private constructor() {
    this.loadHistoryFromStorage();
  }

  public static getInstance(): HistoryManager {
    if (!HistoryManager.instance) {
      HistoryManager.instance = new HistoryManager();
    }
    return HistoryManager.instance;
  }

  // Загрузка истории из localStorage
  private loadHistoryFromStorage(): void {
    try {
      const stored = localStorage.getItem('pediatrics_history');
      if (stored) {
        this.history = JSON.parse(stored).map((item: any) => ({
          ...item,
          timestamp: new Date(item.timestamp)
        }));
      }
    } catch (error) {
      console.error('Ошибка загрузки истории:', error);
      this.history = [];
    }
  }

  // Сохранение истории в localStorage
  private saveHistoryToStorage(): void {
    try {
      localStorage.setItem('pediatrics_history', JSON.stringify(this.history));
    } catch (error) {
      console.error('Ошибка сохранения истории:', error);
    }
  }

  // Добавление записи в историю
  public addHistoryItem(item: Omit<HistoryItem, 'id' | 'timestamp'>): string {
    const newItem: HistoryItem = {
      ...item,
      id: this.generateId(),
      timestamp: new Date()
    };

    this.history.unshift(newItem); // Добавляем в начало списка
    
    // Ограничиваем количество записей (последние 100)
    if (this.history.length > 100) {
      this.history = this.history.slice(0, 100);
    }

    this.saveHistoryToStorage();
    return newItem.id;
  }

  // Получение истории пользователя
  public getUserHistory(userId: string, limit?: number): HistoryItem[] {
    const userHistory = this.history.filter(item => item.userId === userId);
    return limit ? userHistory.slice(0, limit) : userHistory;
  }

  // Получение истории по типу действия
  public getHistoryByAction(userId: string, action: HistoryItem['action']): HistoryItem[] {
    return this.history.filter(item => 
      item.userId === userId && item.action === action
    );
  }

  // Получение избранных записей
  public getFavorites(userId: string): HistoryItem[] {
    return this.history.filter(item => 
      item.userId === userId && item.isFavorite
    );
  }

  // Переключение статуса избранного
  public toggleFavorite(itemId: string): boolean {
    const item = this.history.find(item => item.id === itemId);
    if (item) {
      item.isFavorite = !item.isFavorite;
      this.saveHistoryToStorage();
      return item.isFavorite;
    }
    return false;
  }

  // Удаление записи из истории
  public removeHistoryItem(itemId: string): boolean {
    const index = this.history.findIndex(item => item.id === itemId);
    if (index >= 0) {
      this.history.splice(index, 1);
      this.saveHistoryToStorage();
      return true;
    }
    return false;
  }

  // Очистка истории пользователя
  public clearUserHistory(userId: string): void {
    this.history = this.history.filter(item => item.userId !== userId);
    this.saveHistoryToStorage();
  }

  // Поиск в истории
  public searchHistory(userId: string, query: string): HistoryItem[] {
    const lowerQuery = query.toLowerCase();
    return this.history.filter(item => 
      item.userId === userId && (
        item.title.toLowerCase().includes(lowerQuery) ||
        item.description.toLowerCase().includes(lowerQuery) ||
        item.section.toLowerCase().includes(lowerQuery)
      )
    );
  }

  // Получение статистики истории
  public getHistoryStats(userId: string): {
    totalItems: number;
    favoritesCount: number;
    byAction: Record<string, number>;
    recentActivity: number; // количество действий за последние 7 дней
  } {
    const userHistory = this.history.filter(item => item.userId === userId);
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    const byAction: Record<string, number> = {};
    userHistory.forEach(item => {
      byAction[item.action] = (byAction[item.action] || 0) + 1;
    });

    return {
      totalItems: userHistory.length,
      favoritesCount: userHistory.filter(item => item.isFavorite).length,
      byAction,
      recentActivity: userHistory.filter(item => 
        item.timestamp >= sevenDaysAgo
      ).length
    };
  }

  // Экспорт истории пользователя
  public exportUserHistory(userId: string): string {
    const userHistory = this.history.filter(item => item.userId === userId);
    return JSON.stringify(userHistory, null, 2);
  }

  // Генерация ID
  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
}

// Экспорт глобального экземпляра
export const historyManager = HistoryManager.getInstance();
